import { Addon } from '@galaxy/marketplace-apps/v1';
import { AppKey } from '@vendasta/marketplace-apps';
import { SharedMarketingInformation } from '@vendasta/marketplace-apps';
import { GenericStoreItem, StoreProduct, StoreResellerItem } from './store';

export type GenericItem = GenericStoreItem | Addon | GenericProduct;

export interface GenericProduct {
  appId: string;
  addonId?: string;
  name: string;
  icon: string;
  tagline?: string;
}

export function applyItemWhitelabel<T extends GenericItem>(
  item: T,
  whitelabel: SharedMarketingInformation,
  parentWhitelabel?: SharedMarketingInformation | null,
): T {
  if (isProduct(item)) {
    item.name = whitelabel.name;
    item.iconUrl = whitelabel.iconUrl;
    item.headerImageUrl = whitelabel.bannerImageUrl;
    item.tagline = whitelabel.tagline;
  }

  if (isAddon(item)) {
    if (parentWhitelabel) {
      item.productName = parentWhitelabel.name;
      item.productIcon = parentWhitelabel.iconUrl;
    }
    item.title = whitelabel.name;
    item.icon = whitelabel.iconUrl;
    item.tagline = whitelabel.tagline;
    item.bannerImage = whitelabel.bannerImageUrl;
  }

  if (isResellerItem(item)) {
    item.name = whitelabel.name;
    item.icon = whitelabel.iconUrl;
    item.headerImage = whitelabel.bannerImageUrl;
    item.tagline = whitelabel.tagline;
  }

  if (isGenericProduct(item)) {
    item.name = whitelabel.name;
    item.icon = whitelabel.iconUrl;
    if ('tagline' in item) {
      item.tagline = whitelabel.tagline;
    }
  }

  return item;
}

export function isProduct(item: GenericItem): item is StoreProduct {
  return 'productId' in item;
}

export function isAddon(item: GenericItem): item is Addon {
  return 'addonId' in item && 'productName' in item;
}

export function isResellerItem(item: GenericItem): item is StoreResellerItem {
  return ('appId' in item || 'addonId' in item) && 'headerImage' in item;
}

export function isGenericProduct(item: GenericItem): item is GenericProduct {
  return ('appId' in item || 'addonId' in item) && !('headerImage' in item) && !('bannerImage' in item);
}

export function getItemKey(item: GenericItem): AppKey {
  if (isProduct(item)) {
    return new AppKey({ appId: item.productId, editionId: item.editionId });
  }

  if (isAddon(item)) {
    return new AppKey({ appId: item.addonId });
  }

  if (isResellerItem(item)) {
    return new AppKey({ appId: item.addonId ?? item.appId, editionId: item.editionId });
  }

  if (isGenericProduct(item)) {
    return new AppKey({ appId: item.addonId ?? item.appId });
  }

  throw new Error('Failed to get AppKey from item: Unknown item type');
}

export function getItemKeys(items: GenericItem[]): AppKey[] {
  return items.map((item) => getItemKey(item));
}
